import React from 'react';
import { Data, isDataLoading, makeLoadingData, makeLoadedData, isDataError } from '../../types/data-providers';
import { useData, dataFromDataset } from './DataProviderCache';
import * as DeviceManager from '../domain/device-manager';
import { useAuthHelper } from '../../components/AuthContextProvider';
import { PromiseType } from '../../types/general';


type ContextState = Data<DeviceData>;
export type DeviceData = PromiseType<ReturnType<typeof DeviceManager.getDeviceCondition>> & {
    id: string;
};

const Context = React.createContext<ContextState | null>(null);

function useDeviceData(): ContextState {
    const contextState = React.useContext(Context);
    if (contextState === null) {
        throw new Error('useDeviceInformationData must be used within a DeviceDataProvider');
    }
    return contextState;
}

const DeviceDataProvider: React.FC<{ deviceId: string }> = (props) => {
    const authHelper = useAuthHelper();
    const { dataset } = useData({
        cacheKey: 'device-data',
        id: props.deviceId,
        dataFn: React.useCallback(() => DeviceManager.getDeviceCondition(authHelper, props.deviceId), [
            authHelper,
            props.deviceId
        ]),
    });

    const deviceConditionData = dataFromDataset(dataset);

    const value = React.useMemo(() => {
        if (isDataLoading(deviceConditionData)) {
            return makeLoadingData<DeviceData>();
        }

        return makeLoadedData<DeviceData>({
            id: props.deviceId,
            channelData: isDataError(deviceConditionData) ? deviceConditionData : deviceConditionData.value.channelData,
            deviceProperties: isDataError(deviceConditionData)
                ? deviceConditionData
                : deviceConditionData.value.deviceProperties,
        });
    }, [props.deviceId, deviceConditionData]);

    return <Context.Provider value={value}>{props.children}</Context.Provider>;
};

const TestDeviceDataProvider: React.FC<{ value: ContextState }> = (props) => (
    <Context.Provider value={props.value}>{props.children}</Context.Provider>
);

export { DeviceDataProvider, TestDeviceDataProvider, useDeviceData };
